/* ============================================================
   custom-collage.js  —  Spark Store Custom Photo Collage Poster
   ============================================================ */
(function () {
  'use strict';

  /* ── CONFIG ───────────────────────────────────── */
  var SIZE_CONFIG = {
    '20*30': { ratio: 'cc-ratio-2-3', wCm: 20, hCm: 30 },
    '30*40': { ratio: 'cc-ratio-3-4', wCm: 30, hCm: 40 },
    '40*50': { ratio: 'cc-ratio-4-5', wCm: 40, hCm: 50 },
    '50*70': { ratio: 'cc-ratio-5-7', wCm: 50, hCm: 70 },
  };
  var DEFAULT_SIZE = '20*30';
  var SAFE_CM      = 1.25;
  var MIN_DPI      = 150;
  var GAP_PX       = 3;
  var PAD_PX       = 16;

  /* The customiser runs in five steps. Photos are uploaded and arranged first;
     cropping only opens once the print size and the photo count are both
     settled, because those two together decide the shape of every cell. Crop
     any earlier and the frame you cropped to is not the frame that prints. */
  var STEP_PHOTOS = 1,
      STEP_TEXT   = 2,
      STEP_FRAME  = 3,
      STEP_CROP   = 4,
      STEP_REVIEW = 5;
  var TOTAL_STEPS = 5;

  var FILTER_CSS = {
    bw:    'grayscale(100%)',
    sepia: 'sepia(80%)',
    color: 'none',
  };
  var FILTER_LABEL = { bw: 'Black & White', sepia: 'Sepia', color: 'Color' };

  var BG_CONFIG = {
    white: { bg: '#ffffff', text: '#1a1a1a', muted: '#555555', cell: '#e8e5e0' },
    black: { bg: '#1a1a1a', text: '#f5f5f0', muted: '#aaaaaa', cell: '#2e2e2e' },
    ivory: { bg: '#f5f0e8', text: '#2a2015', muted: '#7a6e5f', cell: '#e0d8cc' },
  };

  /* ── STATE ────────────────────────────────────── */
  /* A photo slot is { file, url, natW, natH, crop }.
     `crop` is a rectangle in the source image's own pixels — null means "centre
     it automatically". Storing the source rectangle rather than a finished
     thumbnail is what lets a crop survive a change of size or photo count: the
     rectangle is simply refitted to the new cell shape. */
  var photos = new Array(12).fill(null);
  var state = {
    step: STEP_PHOTOS,
    filter: 'bw',
    bg: 'white',
    gridCount: 9,
    selectedOptions: {},
    currentSize: DEFAULT_SIZE,
    cropIdx: -1,
  };
  var watermarkRaf = null;

  function g(id) { return document.getElementById(id); }
  function clampNum(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }
  function safePx(px, cm) { return (SAFE_CM / cm) * px; }

  /* ── IMAGE CACHE ───────────────────────────────── */
  /* Every repaint used to build a fresh Image and wait on its decode, so a
     single size change re-decoded twelve photos. Decoded images are held per
     object URL and dropped when that URL is revoked.

     NOTE: this calls back SYNCHRONOUSLY on a cache hit. Any caller counting
     completions must fix its total before starting the first load. */
  var imgCache = Object.create(null);

  function loadImage(url, cb) {
    if (!url) return cb(null);
    var cached = imgCache[url];
    if (cached && cached.complete && cached.naturalWidth) return cb(cached);
    var img = new Image();
    img.onload  = function () { imgCache[url] = img; cb(img); };
    img.onerror = function () { cb(null); };
    img.src = url;
  }

  function releaseUrl(url) {
    if (!url) return;
    delete imgCache[url];
    try { URL.revokeObjectURL(url); } catch (e) {}
  }

  /* ── CROP MATHS ────────────────────────────────── */
  /* Canvas filters are hardware-accelerated and, unlike the old per-pixel loop,
     produce exactly what the CSS filter on the live preview produces — so the
     preview and the printed file finally agree on what "sepia" means. */
  var CANVAS_FILTER_OK = (function () {
    try {
      var c = document.createElement('canvas').getContext('2d');
      c.filter = 'grayscale(100%)';
      return c.filter === 'grayscale(100%)';
    } catch (e) { return false; }
  })();

  /* The centred crop: take the largest rectangle of the requested shape that
     fits inside the photo, anchored in the middle. This is what every photo
     gets until the customer says otherwise, and what it returns to on reset. */
  function autoCrop(natW, natH, aspect) {
    var w, h;
    if (natW / natH > aspect) { h = natH; w = h * aspect; }
    else                      { w = natW; h = w / aspect; }
    return { x: (natW - w) / 2, y: (natH - h) / 2, w: w, h: h };
  }

  /* Refit an existing crop to a new cell shape, keeping the part the customer
     framed in the middle of the shot. Only the rectangle changes shape — the
     subject stays put, which is why changing size or photo count re-crops
     sensibly instead of squashing. */
  function refitCrop(c, natW, natH, aspect) {
    var cx = c.x + c.w / 2, cy = c.y + c.h / 2;
    var h = c.h, w = h * aspect;
    if (w > natW) { w = natW; h = w / aspect; }
    if (h > natH) { h = natH; w = h * aspect; }
    return {
      x: clampNum(cx - w / 2, 0, Math.max(0, natW - w)),
      y: clampNum(cy - h / 2, 0, Math.max(0, natH - h)),
      w: w,
      h: h,
    };
  }

  /* The source rectangle to draw for a photo. It is always exactly the shape of
     the box it is going into, so the destination is filled without ever
     stretching the pixels — the image is cropped instead.

     This never writes back. Several places paint the same photo into boxes that
     can differ by a fraction of a percent (a poster cell, a crop tile, the
     print canvas); if each one stored its own refit they would overwrite each
     other's answer on every repaint. The stored crop is the customer's
     intention, and it is only rewritten when the geometry genuinely changes. */
  function cropFor(p, aspect) {
    if (!p || !p.natW || !p.natH || !aspect || !isFinite(aspect)) return null;
    if (!p.crop) return autoCrop(p.natW, p.natH, aspect);
    if (Math.abs(p.crop.w / p.crop.h - aspect) > 0.002) {
      return refitCrop(p.crop, p.natW, p.natH, aspect);
    }
    return p.crop;
  }

  /* Called when the print size or the photo count moves — the one moment the
     stored crop really does need to become a different shape. */
  function refitStoredCrops(aspect) {
    for (var i = 0; i < photos.length; i++) {
      var p = photos[i];
      if (!p || !p.crop || !p.natW || !p.natH) continue;
      p.crop = refitCrop(p.crop, p.natW, p.natH, aspect);
    }
  }

  /* The frame mockup's thickness is reserved as padding at all times, so the
     poster's width does not depend on whether the frame is showing. This is
     kept as a guard for measurement all the same. */
  function withFrameOff(fn) {
    var shell = document.querySelector('.cc-poster-shell.cc-framed');
    if (shell) shell.classList.remove('cc-framed');
    try { return fn(); }
    finally { if (shell) shell.classList.add('cc-framed'); }
  }

  /* One cell's shape, read off the live preview. The print renderer measures
     the very same cells, so the shape a photo is cropped to is the shape it
     is printed at. */
  function cellAspect() {
    return withFrameOff(function () {
      var cell = g('cc-poster-cell-0');
      if (cell && cell.offsetWidth > 0 && cell.offsetHeight > 0) {
        return cell.offsetWidth / cell.offsetHeight;
      }
      /* Before first layout: derive it from the poster's own proportions. */
      var cfg  = SIZE_CONFIG[state.currentSize] || SIZE_CONFIG[DEFAULT_SIZE];
      var spec = gridSpec(state.gridCount);
      var w = 340 - PAD_PX * 2;
      var h = 340 * cfg.hCm / cfg.wCm - PAD_PX * 2 - 90;
      return ((w - GAP_PX * (spec.cols - 1)) / spec.cols) /
             ((h - GAP_PX * (spec.rows - 1)) / spec.rows);
    });
  }

  function drawPhoto(ctx, img, rect, dx, dy, dw, dh, filter) {
    var css = FILTER_CSS[filter] || 'none';
    if (css === 'none') {
      ctx.drawImage(img, rect.x, rect.y, rect.w, rect.h, dx, dy, dw, dh);
      return;
    }
    if (CANVAS_FILTER_OK) {
      ctx.save();
      ctx.filter = css;
      ctx.drawImage(img, rect.x, rect.y, rect.w, rect.h, dx, dy, dw, dh);
      ctx.restore();
      return;
    }
    /* Fallback for engines without canvas filters. */
    var tmp  = document.createElement('canvas');
    tmp.width = Math.max(1, Math.ceil(dw));
    tmp.height = Math.max(1, Math.ceil(dh));
    var tctx = tmp.getContext('2d');
    tctx.drawImage(img, rect.x, rect.y, rect.w, rect.h, 0, 0, tmp.width, tmp.height);
    var data = tctx.getImageData(0, 0, tmp.width, tmp.height);
    var d = data.data;
    for (var i = 0; i < d.length; i += 4) {
      var r = d[i], gg = d[i + 1], b = d[i + 2];
      if (filter === 'bw') {
        var l = 0.299 * r + 0.587 * gg + 0.114 * b;
        d[i] = d[i + 1] = d[i + 2] = l;
      } else {
        d[i]     = Math.min(255, 0.393 * r + 0.769 * gg + 0.189 * b);
        d[i + 1] = Math.min(255, 0.349 * r + 0.686 * gg + 0.168 * b);
        d[i + 2] = Math.min(255, 0.272 * r + 0.534 * gg + 0.131 * b);
      }
    }
    tctx.putImageData(data, 0, 0);
    ctx.drawImage(tmp, dx, dy, dw, dh);
  }

  /* Paint one photo into a canvas that fills a box of the given CSS size.
     The pixel buffer is always sized from the box it is about to occupy, which
     is the whole point: a canvas whose buffer disagrees with its CSS size is
     exactly what stretched the photos before. */
  function paintPhotoCanvas(canvas, idx, cssW, cssH, done) {
    var p = photos[idx];
    if (!canvas || !p || !cssW || !cssH) { if (done) done(false); return; }
    loadImage(p.url, function (img) {
      if (!img || photos[idx] !== p) { if (done) done(false); return; }
      if (!p.natW) { p.natW = img.naturalWidth; p.natH = img.naturalHeight; }
      var rect = cropFor(p, cssW / cssH);
      if (!rect) { if (done) done(false); return; }
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width  = Math.max(1, Math.round(cssW * dpr));
      canvas.height = Math.max(1, Math.round(cssH * dpr));
      canvas.style.display = 'block';
      var ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      drawPhoto(ctx, img, rect, 0, 0, canvas.width, canvas.height, state.filter);
      if (done) done(true);
    });
  }

  /* ── POSTER CELL PAINTING ────────────────────────── */
  var dirtyCells = {};
  var cellRaf = null;

  function markCellDirty(idx) { dirtyCells[idx] = true; }

  function scheduleCellRepaint() {
    if (cellRaf) return;
    cellRaf = requestAnimationFrame(function () {
      cellRaf = null;
      var pending = dirtyCells;
      dirtyCells = {};
      for (var key in pending) {
        if (Object.prototype.hasOwnProperty.call(pending, key)) paintPosterCell(parseInt(key, 10));
      }
    });
  }

  function repaintAllPosterCells() {
    for (var i = 0; i < 12; i++) markCellDirty(i);
    scheduleCellRepaint();
  }

  function paintPosterCell(idx) {
    var cell = g('cc-poster-cell-' + idx);
    if (!cell) return;
    var canvas = cell.querySelector('.cc-poster-cell-canvas');
    if (!photos[idx] || idx >= state.gridCount) {
      if (canvas) canvas.style.display = 'none';
      return;
    }
    if (!canvas) {
      canvas = document.createElement('canvas');
      canvas.className = 'cc-poster-cell-canvas';
      cell.appendChild(canvas);
    }
    paintPhotoCanvas(canvas, idx, cell.offsetWidth, cell.offsetHeight);
  }

  /* ── SPINNER ─────────────────────────────────── */
  function showSpinner(btn) {
    var orig = btn.innerHTML;
    btn.innerHTML =
      '<span style="display:inline-flex;align-items:center;gap:8px;">' +
      '<svg width="16" height="16" viewBox="0 0 16 16" style="animation:cc-spin 0.7s linear infinite;flex-shrink:0;">' +
      '<circle cx="8" cy="8" r="6" fill="none" stroke="rgba(255,255,255,0.3)" stroke-width="2"/>' +
      '<path d="M8 2 A6 6 0 0 1 14 8" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round"/></svg>' +
      '<span id="cc-spin-label">Working…</span></span>';
    btn.disabled = true; btn.dataset.sparkBusy = '1';
    return {
      label:   function (t) { var l = g('cc-spin-label'); if (l) l.textContent = t; },
      restore: function () { btn.innerHTML = orig; btn.disabled = false; delete btn.dataset.sparkBusy; },
    };
  }

  /* ── INIT ────────────────────────────────────── */
  function init() {
    if (!g('cc-product-form')) return;
    if (!g('cc-spin-style')) {
      var st = document.createElement('style');
      st.id = 'cc-spin-style';
      st.textContent = '@keyframes cc-spin{to{transform:rotate(360deg)}}';
      document.head.appendChild(st);
    }

    document.querySelectorAll('.cc-pill.active[data-option]').forEach(function (p) {
      state.selectedOptions[p.dataset.option] = p.dataset.value;
      if (p.dataset.value in SIZE_CONFIG) state.currentSize = p.dataset.value;
    });

    ['cc-name1', 'cc-name2', 'cc-headline'].forEach(function (id) {
      var el = g(id); if (!el) return;
      var label = el.closest('.cc-field') && el.closest('.cc-field').querySelector('label');
      if (label && !label.querySelector('.cc-required')) {
        var star = document.createElement('span');
        star.className = 'cc-required'; star.textContent = '*';
        label.appendChild(star);
      }
    });

    bindEvents();
    observePosterCells();
    updatePreview();
    resolveVariant();
    goToStep(STEP_PHOTOS);
    scheduleWatermark();
    offerDraft();
  }

  /* Each cell is watched individually. Watching only the poster missed the case
     that mattered most: switching between 9 and 12 photos reshapes every cell
     without changing the poster's own size at all. */
  function observePosterCells() {
    if (!window.ResizeObserver) return;
    var ro = new ResizeObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var idx = parseInt(entries[i].target.dataset.cellIdx, 10);
        if (!isNaN(idx)) markCellDirty(idx);
      }
      scheduleCellRepaint();
      scheduleWatermark();
    });
    for (var i = 0; i < 12; i++) {
      var cell = g('cc-poster-cell-' + i);
      if (cell) { cell.dataset.cellIdx = i; ro.observe(cell); }
    }
  }

  /* ── STEPPER ─────────────────────────────────── */
  function goToStep(n) {
    state.step = n;
    for (var i = 1; i <= TOTAL_STEPS; i++) {
      var el = g('cc-step-' + i);
      if (el) el.style.display = (i === n) ? '' : 'none';
    }
    var fill = g('cc-progress-fill');
    if (fill) fill.style.width = ((n / TOTAL_STEPS) * 100) + '%';
    document.querySelectorAll('.cc-step-labels span').forEach(function (s) {
      s.classList.toggle('active', parseInt(s.dataset.step, 10) === n);
    });
    var btnPrev = g('cc-btn-prev'), btnNext = g('cc-btn-next');
    if (btnPrev) btnPrev.style.visibility = (n === 1) ? 'hidden' : 'visible';
    if (btnNext) {
      if (n === TOTAL_STEPS) { btnNext.style.display = 'none'; }
      else {
        btnNext.style.display = '';
        btnNext.textContent = (n === STEP_FRAME) ? 'Crop photos →'
                            : (n === STEP_CROP)  ? 'Review →'
                            : 'Next →';
      }
    }
    if (n === STEP_CROP)   buildCropStep();
    if (n === STEP_REVIEW) buildReview();
    /* The frame snaps in on the Frame & Size step and stays for the rest, so
       the customer crops and reviews against the finished piece. */
    applyFrame(n >= STEP_FRAME, false);
    hideError();
  }

  /* ── VALIDATION ────────────────────────────────── */
  function validateStep(n) {
    if (n === STEP_PHOTOS) {
      var missing = [];
      for (var i = 0; i < state.gridCount; i++) { if (!photos[i]) missing.push(i + 1); }
      if (missing.length > 0) {
        for (var j = 0; j < state.gridCount; j++) {
          var cell = document.querySelector('.cc-cell[data-idx="' + j + '"]');
          if (cell) {
            cell.classList.remove('cc-cell--missing');
            void cell.offsetWidth;
            if (!photos[j]) cell.classList.add('cc-cell--missing');
          }
        }
        showError(missing.length === 1
          ? 'Please upload photo ' + missing[0] + ' before continuing.'
          : 'Please upload all ' + state.gridCount + ' photos. Still missing: ' + missing.join(', ') + '.');
        return false;
      }
    }
    if (n === STEP_TEXT) {
      var name1    = (g('cc-name1') && g('cc-name1').value.trim()) || '';
      var name2    = (g('cc-name2') && g('cc-name2').value.trim()) || '';
      var headline = (g('cc-headline') && g('cc-headline').value.trim()) || '';
      if (!name1 || !name2) {
        if (!name1 && g('cc-name1')) g('cc-name1').classList.add('cc-field--error');
        if (!name2 && g('cc-name2')) g('cc-name2').classList.add('cc-field--error');
        showError('Please enter both names before continuing.');
        return false;
      }
      if (!headline) {
        if (g('cc-headline')) g('cc-headline').classList.add('cc-field--error');
        showError('Please enter a headline before continuing.');
        return false;
      }
    }
    /* Cropping is never required — every photo already has a sensible centred
       crop, so the step is an invitation, not a gate. */
    return true;
  }

  /* ── GRID COUNT ────────────────────────────────── */
  /* Single source of truth for how each photo count is laid out, labelled and
     recorded on the order. */
  function gridSpec(n) {
    if (n === 1)  return { cols: 1, rows: 1, label: '1 photo',           prop: '1x1 (1 photo)' };
    if (n === 12) return { cols: 3, rows: 4, label: '3 × 4 (12 photos)', prop: '3x4 (12 photos)' };
    return              { cols: 3, rows: 3, label: '3 × 3 (9 photos)',  prop: '3x3 (9 photos)' };
  }

  function setGridCount(n) {
    state.gridCount = n;
    var spec = gridSpec(n);
    for (var i = 0; i < 12; i++) {
      var cell = document.querySelector('.cc-cell[data-idx="' + i + '"]');
      if (cell) {
        cell.classList.toggle('cc-cell--hidden', i >= n);
        cell.classList.remove('cc-cell--missing');
      }
    }
    var pg = g('cc-poster-grid');
    if (pg) {
      pg.classList.toggle('cc-grid-12', n === 12);
      pg.classList.toggle('cc-grid-1',  n === 1);
    }
    var cg = g('cc-photo-grid');
    if (cg) cg.classList.toggle('cc-photo-grid--single', n === 1);
    for (var k = 0; k < 12; k++) {
      var pc = g('cc-poster-cell-' + k);
      if (pc) pc.classList.toggle('cc-poster-cell--hidden', k >= n);
    }
    var tc = g('cc-trigger-count'), tc2 = g('cc-trigger-count2'), ut = g('cc-upload-total');
    if (tc)  tc.textContent  = n;
    if (tc2) tc2.textContent = n;
    if (ut)  ut.textContent  = n;
    var tb = g('cc-upload-trigger');
    if (tb) tb.innerHTML = n === 1
      ? '📂 &nbsp;Choose Your Photo'
      : '📂 &nbsp;Choose <span id="cc-trigger-count">' + n + '</span> Photos';
    var hint = document.querySelector('.cc-upload-trigger-hint');
    if (hint) hint.innerHTML = n === 1
      ? 'Pick the one photo you want printed'
      : 'All photos open at once — select exactly <span id="cc-trigger-count2">' + n + '</span>';
    var prop = g('cc-prop-grid');
    if (prop) prop.value = spec.prop;
    updateUploadCount();
    onGeometryChanged();
    scheduleDraftSave();
  }

  /* Print size and photo count are the two things that decide a cell's shape.
     Whenever either moves, every crop is refitted and every cell repainted, so
     the poster is always showing the crop that will actually print. */
  function onGeometryChanged() {
    requestAnimationFrame(function () {
      var aspect = cellAspect();
      refitStoredCrops(aspect);
      repaintAllPosterCells();
      scheduleWatermark();
      updateQuality();
      if (state.step === STEP_CROP) buildCropStep();
    });
  }

  /* ── RESOLUTION GUARD ────────────────────────────
     Each photo fills one cell, so sharpness is judged against that cell's
     physical size — measured off the live preview rather than estimated. */
  function cellPrintCm() {
    var cfg    = SIZE_CONFIG[state.currentSize] || SIZE_CONFIG[DEFAULT_SIZE];
    var poster = g('cc-poster'), cell = g('cc-poster-cell-0');
    if (poster && cell && poster.offsetWidth && poster.offsetHeight && cell.offsetWidth) {
      return {
        w: cfg.wCm * (cell.offsetWidth  / poster.offsetWidth),
        h: cfg.hCm * (cell.offsetHeight / poster.offsetHeight),
      };
    }
    var spec = gridSpec(state.gridCount);
    return { w: (cfg.wCm * 0.90) / spec.cols, h: (cfg.hCm * 0.72) / spec.rows };
  }

  function assessPhoto(idx, aspect) {
    var p = photos[idx];
    if (!p || !p.natW || !p.natH) return null;
    var cell = cellPrintCm();
    /* Only the cropped region reaches the printer, so judge it on that — a
       4000px photo cropped down to 800px prints as an 800px photo. */
    var rect = cropFor(p, aspect || cellAspect());
    if (!rect) return null;
    var dpi = Math.min(rect.w / (cell.w / 2.54), rect.h / (cell.h / 2.54));
    return { dpi: Math.round(dpi), ratio: dpi / MIN_DPI };
  }

  function updateQuality() {
    var el = g('cc-quality');
    var counted = 0, worstRatio = null, worstDpi = 0, low = [];
    var aspect = cellAspect();

    for (var i = 0; i < state.gridCount; i++) {
      var a    = assessPhoto(i, aspect);
      var cell = document.querySelector('.cc-cell[data-idx="' + i + '"]');
      var bad  = !!(a && a.ratio < 0.75);
      if (cell) cell.classList.toggle('cc-cell--lowres', bad);
      if (!a) continue;
      counted++;
      if (worstRatio === null || a.ratio < worstRatio) { worstRatio = a.ratio; worstDpi = a.dpi; }
      if (bad) low.push(i + 1);
    }

    var prop = g('cc-prop-quality');
    if (!el) return;
    if (!counted) {
      el.style.display = 'none';
      if (prop) prop.value = '';
      return;
    }

    var level, icon, msg;
    if (worstRatio >= 1)         { level = 'good';    icon = '✓'; msg = 'Great resolution for ' + state.currentSize + ' cm.'; }
    else if (worstRatio >= 0.75) { level = 'good';    icon = '✓'; msg = 'Good resolution for ' + state.currentSize + ' cm.'; }
    else if (worstRatio >= 0.5)  { level = 'low';     icon = '!'; msg = 'Photo' + (low.length > 1 ? 's ' : ' ') + low.join(', ') + ' may look soft at ' + state.currentSize + ' cm. A larger file, a tighter-out crop or a smaller size will print sharper.'; }
    else                         { level = 'verylow'; icon = '!'; msg = 'Photo' + (low.length > 1 ? 's ' : ' ') + low.join(', ') + ' are quite low resolution for ' + state.currentSize + ' cm and will look blurry. We recommend replacing them or choosing a smaller size.'; }

    el.className = 'cc-quality cc-quality--' + level;
    el.style.display = '';
    el.innerHTML = '<span class="cc-quality-icon">' + icon + '</span><span>' + msg + '</span>';

    if (prop) {
      prop.value = (level === 'good' ? 'OK' : level === 'low' ? 'Low' : 'Very low') +
                   ' (~' + worstDpi + ' DPI at ' + state.currentSize + ' cm)';
    }
  }

  /* ── HEADLINE PRESETS ───────────────────────────── */
  function applyPhraseSelection(focusCustom) {
    var sel = g('cc-phrase');
    if (!sel) return;
    var wrap = g('cc-custom-text');
    var head = g('cc-headline');
    var sub  = g('cc-subline');
    var edit = g('cc-phrase-edit');
    var isCustom = sel.value === 'custom';

    if (wrap) wrap.style.display = isCustom ? '' : 'none';
    if (edit) edit.style.display = isCustom ? 'none' : '';

    if (isCustom) {
      if (focusCustom) {
        if (head) { head.value = ''; head.focus(); }
        if (sub)  sub.value = '';
      }
    } else {
      var opt = sel.options[sel.selectedIndex];
      if (head) head.value = (opt && opt.dataset.headline) || '';
      if (sub)  sub.value  = (opt && opt.dataset.subline)  || '';
    }
    updatePreview();
  }

  function initPhrasePresets() {
    var sel = g('cc-phrase');
    if (!sel) return;
    sel.addEventListener('change', function () { applyPhraseSelection(true); });

    var edit = g('cc-phrase-edit');
    if (edit) edit.addEventListener('click', function () {
      var head = g('cc-headline'), sub = g('cc-subline');
      var opt  = sel.options[sel.selectedIndex];
      var h = (opt && opt.dataset.headline) || '';
      var s = (opt && opt.dataset.subline)  || '';
      sel.value = 'custom';
      applyPhraseSelection(false);
      if (head) { head.value = h; head.focus(); }
      if (sub)  sub.value = s;
      updatePreview();
    });

    applyPhraseSelection(false);
  }

  /* ── CROP STEP ─────────────────────────────────
     Reachable only once the size and the photo count are chosen. Until then
     the cell shape is unknown, and a crop made against the wrong shape has to
     be thrown away. Every tile is drawn at the exact printing shape. */
  function croppedCount() {
    var n = 0;
    for (var i = 0; i < state.gridCount; i++) { if (photos[i] && photos[i].crop) n++; }
    return n;
  }

  function buildCropStep() {
    var wrap = g('cc-crop-grid');
    if (!wrap) return;
    var n      = state.gridCount;
    var aspect = cellAspect();

    wrap.classList.toggle('cc-crop-grid--single', n === 1);
    wrap.style.setProperty('--cc-crop-aspect', aspect.toFixed(4));
    wrap.innerHTML = '';

    for (var i = 0; i < n; i++) {
      var tile = document.createElement('button');
      tile.type = 'button';
      tile.className = 'cc-crop-tile';
      tile.dataset.idx = i;
      tile.setAttribute('aria-label', 'Adjust the crop of photo ' + (i + 1));

      var canvas = document.createElement('canvas');
      canvas.className = 'cc-crop-tile-canvas';
      tile.appendChild(canvas);

      var num = document.createElement('span');
      num.className = 'cc-crop-tile-num';
      num.textContent = i + 1;
      tile.appendChild(num);

      var tag = document.createElement('span');
      tag.className = 'cc-crop-tile-tag';
      tag.textContent = photos[i] && photos[i].crop ? 'Adjusted' : 'Centred';
      tile.appendChild(tag);

      var hint = document.createElement('span');
      hint.className = 'cc-crop-tile-hint';
      hint.textContent = 'Adjust';
      tile.appendChild(hint);

      wrap.appendChild(tile);
    }

    requestAnimationFrame(paintCropTiles);
    updateCropSummary();
  }

  function paintCropTiles() {
    var wrap = g('cc-crop-grid');
    if (!wrap) return;
    wrap.querySelectorAll('.cc-crop-tile').forEach(function (tile) {
      var idx    = parseInt(tile.dataset.idx, 10);
      var canvas = tile.querySelector('.cc-crop-tile-canvas');
      paintPhotoCanvas(canvas, idx, tile.clientWidth, tile.clientHeight);
    });
  }

  function updateCropSummary() {
    var el = g('cc-crop-summary');
    if (!el) return;
    var cell = cellPrintCm();
    var done = croppedCount();
    var each = state.gridCount === 1 ? 'Your photo prints' : 'Each photo prints';
    el.innerHTML =
      '<strong>' + each + ' at about ' + cell.w.toFixed(1) + ' × ' + cell.h.toFixed(1) + ' cm</strong>' +
      ' — every photo is centred automatically, so you only need to step in where you want something different.' +
      (done ? ' <span class="cc-crop-done">' + done + ' of ' + state.gridCount + ' adjusted by you.</span>' : '');

    var resetAll = g('cc-crop-reset-all');
    if (resetAll) resetAll.style.display = done ? '' : 'none';
  }

  function resetAllCrops() {
    for (var i = 0; i < state.gridCount; i++) { if (photos[i]) photos[i].crop = null; }
    repaintAllPosterCells();
    buildCropStep();
    updateQuality();
    scheduleDraftSave();
  }

  /* ── CROPPER MODAL ───────────────────────────────
     cropper.js drives it, configured so the crop frame never moves: the
     customer drags and zooms the photo behind a fixed window, the way every
     phone gallery works. Nothing to resize, nothing to line up, no way to
     produce a crop that is the wrong shape for the cell. */
  var cropper = null;

  function cropperAvailable() { return typeof window.Cropper === 'function'; }

  function openCropper(idx) {
    var modal = g('cc-cropper-modal');
    var imgEl = g('cc-cropper-img');
    if (!modal || !imgEl || !photos[idx]) return;

    if (!cropperAvailable()) {
      showError('The crop tool could not load. Your photos are centred automatically, so you can carry on and order.');
      return;
    }

    state.cropIdx = idx;

    var p      = photos[idx];
    var aspect = cellAspect();
    var rect   = cropFor(p, aspect);

    destroyCropper();

    var title = g('cc-cropper-title');
    if (title) title.textContent = state.gridCount === 1
      ? 'Adjust your photo'
      : 'Photo ' + (idx + 1) + ' of ' + state.gridCount;

    modal.style.display = 'flex';
    document.body.style.overflow = 'hidden';
    updateCropperNav();

    imgEl.src = p.url;
    cropper = new window.Cropper(imgEl, {
      viewMode: 1,
      aspectRatio: aspect,
      dragMode: 'move',
      autoCropArea: 1,
      cropBoxMovable: false,
      cropBoxResizable: false,
      toggleDragModeOnDblclick: false,
      guides: true,
      center: true,
      highlight: false,
      background: false,
      responsive: true,
      restore: false,
      /* Browsers already honour EXIF orientation when decoding, so letting
         cropper.js correct it again would rotate portrait photos twice. */
      checkOrientation: false,
      ready: function () {
        if (rect) cropper.setData({ x: rect.x, y: rect.y, width: rect.w, height: rect.h });
        syncZoomSlider();
      },
      /* Zooming out past "fills the window" would leave the poster's background
         showing through the photo, so that is where the floor is. */
      zoom: function (e) {
        var b = zoomBounds();
        if (b && e.detail.ratio < b.fit) { e.preventDefault(); return; }
        syncZoomSlider();
      },
      cropend: syncZoomSlider,
    });
  }

  function destroyCropper() {
    if (cropper) { try { cropper.destroy(); } catch (e) {} cropper = null; }
  }

  /* The slider is expressed as a multiple of "fills the window exactly", which
     is the only zoom level a customer actually thinks in. */
  function zoomBounds() {
    if (!cropper) return null;
    var img  = cropper.getImageData();
    var box  = cropper.getCropBoxData();
    if (!img || !img.naturalWidth || !box || !box.width) return null;
    var fit = Math.max(box.width / img.naturalWidth, box.height / img.naturalHeight);
    return { fit: fit, current: img.width / img.naturalWidth };
  }

  var syncingZoom = false;
  function syncZoomSlider() {
    var slider = g('cc-cropper-zoom');
    var b = zoomBounds();
    if (!slider || !b) return;
    syncingZoom = true;
    slider.value = clampNum(b.current / b.fit, 1, 4).toFixed(3);
    syncingZoom = false;
  }

  function applyZoomSlider() {
    var slider = g('cc-cropper-zoom');
    var b = zoomBounds();
    if (!slider || !b || syncingZoom || !cropper) return;
    cropper.zoomTo(b.fit * parseFloat(slider.value));
  }

  function commitCrop() {
    if (!cropper || state.cropIdx < 0) return;
    var p = photos[state.cropIdx];
    if (!p) return;
    var d = cropper.getData(true);
    if (d && d.width > 0 && d.height > 0) {
      /* getData can run a pixel or two past the edge when the image is dragged
         hard against it; clamp so the rectangle is always inside the photo. */
      var w = clampNum(d.width,  1, p.natW);
      var h = clampNum(d.height, 1, p.natH);
      p.crop = {
        x: clampNum(d.x, 0, Math.max(0, p.natW - w)),
        y: clampNum(d.y, 0, Math.max(0, p.natH - h)),
        w: w,
        h: h,
      };
    }
    markCellDirty(state.cropIdx);
    scheduleCellRepaint();
    scheduleDraftSave();
  }

  function closeCropper() {
    var modal = g('cc-cropper-modal');
    if (modal) modal.style.display = 'none';
    document.body.style.overflow = '';
    destroyCropper();
    var imgEl = g('cc-cropper-img');
    if (imgEl) imgEl.removeAttribute('src');
    state.cropIdx = -1;
    buildCropStep();
    updateQuality();
  }

  function nextPhotoFrom(idx) {
    for (var i = idx + 1; i < state.gridCount; i++) { if (photos[i]) return i; }
    return -1;
  }

  function updateCropperNav() {
    var prev = g('cc-cropper-prev');
    var next = g('cc-cropper-next');
    var count = g('cc-cropper-count');
    var idx = state.cropIdx;
    if (prev) prev.disabled = idx <= 0;
    if (next) {
      next.textContent = nextPhotoFrom(idx) >= 0 ? 'Save & next →' : 'Save & finish';
    }
    if (count) {
      count.textContent = state.gridCount === 1
        ? ''
        : 'Photo ' + (idx + 1) + ' / ' + state.gridCount;
    }
  }

  function stepCropper(delta) {
    commitCrop();
    var target = state.cropIdx + delta;
    while (target >= 0 && target < state.gridCount && !photos[target]) target += delta;
    if (target < 0 || target >= state.gridCount) { closeCropper(); return; }
    openCropper(target);
  }

  /* ── REVIEW ──────────────────────────────────── */
  function buildReview() {
    var v = getValues();
    var uploaded = 0;
    for (var i = 0; i < state.gridCount; i++) { if (photos[i]) uploaded++; }
    var summary = g('cc-review-summary'); if (!summary) return;
    var fl = FILTER_LABEL[state.filter] || state.filter;
    var bl = state.bg.charAt(0).toUpperCase() + state.bg.slice(1);
    var done = croppedCount();
    var lines = [
      ['Photos',     uploaded + ' / ' + state.gridCount],
      ['Grid',       gridSpec(state.gridCount).label],
      ['Cropping',   done ? done + ' adjusted, ' + (state.gridCount - done) + ' auto-centred' : 'All auto-centred'],
      ['Names',      v.names],
      /* Left off the poster entirely when blank, so say so rather than
         showing a dash that reads like something went missing. */
      ['Date',       v.date || 'Not shown'],
      ['Headline',   v.headline],
      ['Subline',    v.subline],
      ['Filter',     fl],
      ['Background', bl],
      ['Size',       state.currentSize + ' cm'],
    ];
    summary.innerHTML = lines.map(function (l) {
      return '<div><strong>' + l[0] + '</strong>' + (l[1] || '—') + '</div>';
    }).join('');
    var p = function (id, val) { var el = g(id); if (el) el.value = val; };
    p('cc-prop-names',    v.names);
    p('cc-prop-date',     v.date);
    p('cc-prop-headline', v.headline);
    p('cc-prop-subline',  v.subline);
    p('cc-prop-filter',   fl);
    p('cc-prop-bg',       bl);
    p('cc-prop-grid',     gridSpec(state.gridCount).prop);
    p('cc-prop-crop',     done ? done + ' of ' + state.gridCount + ' manually cropped' : 'All auto-centred');
  }

  /* ── EVENTS ──────────────────────────────────── */
  function bindEvents() {
    ['cc-name1', 'cc-name2', 'cc-sep', 'cc-date', 'cc-headline', 'cc-subline'].forEach(function (id) {
      var el = g(id); if (!el) return;
      el.addEventListener('input', updatePreview);
      el.addEventListener('input', scheduleDraftSave);
      el.addEventListener('input', function () { this.classList.remove('cc-field--error'); });
    });

    document.querySelectorAll('.cc-count-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        document.querySelectorAll('.cc-count-btn').forEach(function (b) { b.classList.remove('active'); });
        this.classList.add('active');
        setGridCount(parseInt(this.dataset.count, 10));
      });
    });

    var trigger = g('cc-upload-trigger');
    var bulkInput = g('cc-bulk-input');
    if (trigger && bulkInput) {
      trigger.addEventListener('click', function () { bulkInput.click(); });
      bulkInput.addEventListener('change', function () {
        var files = Array.from(this.files).filter(function (f) {
          return f.type.startsWith('image/') || /\.hei[cf]$/i.test(f.name);
        });
        if (files.length === 0) return;
        var grid = g('cc-photo-grid'), prog = g('cc-upload-progress');
        if (grid) grid.style.display = '';
        if (prog) prog.style.display = '';
        var queue = files.slice(0, state.gridCount);
        function processNext(i) {
          if (i >= queue.length) {
            if (queue.length < state.gridCount) {
              showError('You selected ' + queue.length + ' photos but need ' + state.gridCount + '. Please upload the remaining ' + (state.gridCount - queue.length) + '.');
            } else {
              hideError();
            }
            return;
          }
          handlePhotoQueued(i, queue[i], function () { processNext(i + 1); });
        }
        processNext(0);
      });
    }

    var shuffleBtn = g('cc-shuffle-btn');
    if (shuffleBtn) shuffleBtn.addEventListener('click', shufflePhotos);
    var undoBtn = g('cc-undo-btn');
    if (undoBtn) undoBtn.addEventListener('click', undoOrder);
    initPhrasePresets();

    document.querySelectorAll('.cc-replace-input').forEach(function (input) {
      input.addEventListener('change', function () {
        var idx = parseInt(this.dataset.idx, 10);
        var file = this.files[0];
        if (file && (file.type.startsWith('image/') || /\.hei[cf]$/i.test(file.name))) handlePhoto(idx, file);
      });
    });

    document.addEventListener('click', function (e) {
      var replace = e.target.closest('.cc-ctrl-replace');
      if (replace) {
        var inp = g('cc-replace-' + replace.dataset.idx);
        if (inp) inp.click();
      }
    });

    /* Crop step */
    var cropGrid = g('cc-crop-grid');
    if (cropGrid) cropGrid.addEventListener('click', function (e) {
      var tile = e.target.closest('.cc-crop-tile');
      if (!tile) return;
      openCropper(parseInt(tile.dataset.idx, 10));
    });

    var cropAll = g('cc-crop-all');
    if (cropAll) cropAll.addEventListener('click', function () {
      for (var i = 0; i < state.gridCount; i++) {
        if (photos[i]) { openCropper(i); return; }
      }
    });

    var resetAll = g('cc-crop-reset-all');
    if (resetAll) resetAll.addEventListener('click', resetAllCrops);

    var cropClose = g('cc-cropper-close');
    if (cropClose) cropClose.addEventListener('click', function () { commitCrop(); closeCropper(); });

    var cropNext = g('cc-cropper-next');
    if (cropNext) cropNext.addEventListener('click', function () {
      if (nextPhotoFrom(state.cropIdx) < 0) { commitCrop(); closeCropper(); return; }
      stepCropper(1);
    });

    var cropPrev = g('cc-cropper-prev');
    if (cropPrev) cropPrev.addEventListener('click', function () { stepCropper(-1); });

    var cropReset = g('cc-cropper-reset');
    if (cropReset) cropReset.addEventListener('click', function () {
      if (!cropper || state.cropIdx < 0) return;
      var p = photos[state.cropIdx];
      p.crop = null;
      var rect = cropFor(p, cellAspect());
      cropper.reset();
      if (rect) cropper.setData({ x: rect.x, y: rect.y, width: rect.w, height: rect.h });
      syncZoomSlider();
    });

    var zoom = g('cc-cropper-zoom');
    if (zoom) zoom.addEventListener('input', applyZoomSlider);

    var cropperModal = g('cc-cropper-modal');
    if (cropperModal) cropperModal.addEventListener('click', function (e) {
      if (e.target === cropperModal) { commitCrop(); closeCropper(); }
    });

    document.addEventListener('keydown', function (e) {
      if (state.cropIdx < 0) return;
      if (e.key === 'Escape') { commitCrop(); closeCropper(); }
      if (e.key === 'Enter')  { e.preventDefault(); commitCrop(); closeCropper(); }
    });

    initDragDrop();

    var filterRow = g('cc-filter-row');
    if (filterRow) filterRow.addEventListener('click', function (e) {
      var btn = e.target.closest('.cc-filter-btn'); if (!btn) return;
      document.querySelectorAll('.cc-filter-btn').forEach(function (b) { b.classList.remove('active'); });
      btn.classList.add('active');
      state.filter = btn.dataset.filter;
      applyFilterToAll();
    });

    var bgRow = g('cc-bg-row');
    if (bgRow) bgRow.addEventListener('click', function (e) {
      var btn = e.target.closest('.cc-bg-btn'); if (!btn) return;
      document.querySelectorAll('.cc-bg-btn').forEach(function (b) { b.classList.remove('active'); });
      btn.classList.add('active');
      state.bg = btn.dataset.bg;
      applyBackground();
    });

    document.querySelectorAll('.cc-pill[data-option]').forEach(function (pill) {
      pill.addEventListener('click', function () {
        if (this.classList.contains('unavailable')) return;
        var opt = this.dataset.option;
        document.querySelectorAll('.cc-pill[data-option="' + opt + '"]').forEach(function (p) { p.classList.remove('active'); });
        this.classList.add('active');
        state.selectedOptions[opt] = this.dataset.value;
        if (this.dataset.value in SIZE_CONFIG) {
          state.currentSize = this.dataset.value;
          updatePosterRatio(this.dataset.value);
          /* A new size is a new cell shape — refit every crop to it. */
          onGeometryChanged();
          scheduleDraftSave();
        }
        resolveVariant();
        applyFrame(state.step >= STEP_FRAME, true);
      });
    });

    var btnNext = g('cc-btn-next'), btnPrev = g('cc-btn-prev');
    if (btnNext) btnNext.addEventListener('click', function () {
      if (!validateStep(state.step)) return;
      if (state.step < TOTAL_STEPS) goToStep(state.step + 1);
    });
    if (btnPrev) btnPrev.addEventListener('click', function () {
      if (state.step > 1) goToStep(state.step - 1);
    });

    window.addEventListener('resize', function () {
      if (state.step === STEP_CROP) requestAnimationFrame(paintCropTiles);
    });

    var form = g('cc-product-form');
    if (form) form.addEventListener('submit', handleSubmit);
  }

  /* ── PHOTO ───────────────────────────────────── */
  function handlePhotoQueued(idx, file, done) {
    var isHeic = file.type === 'image/heic' || file.type === 'image/heif' ||
                 /\.hei[cf]$/i.test(file.name);
    if (isHeic) {
      convertHeicToJpeg(file, function (jpegFile) {
        applyPhoto(idx, jpegFile || file);
        if (done) done();
      });
    } else {
      applyPhoto(idx, file);
      if (done) done();
    }
  }

  function handlePhoto(idx, file) {
    var isHeic = file.type === 'image/heic' || file.type === 'image/heif' ||
                 /\.hei[cf]$/i.test(file.name);
    if (isHeic) {
      convertHeicToJpeg(file, function (jpegFile) {
        if (jpegFile) { handlePhoto(idx, jpegFile); } else { applyPhoto(idx, file); }
      });
      return;
    }
    applyPhoto(idx, file);
  }

  function convertHeicToJpeg(file, callback) {
    var url = URL.createObjectURL(file);
    var img = new Image();
    img.onload = function () {
      try {
        var canvas = document.createElement('canvas');
        canvas.width  = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext('2d').drawImage(img, 0, 0);
        URL.revokeObjectURL(url);
        canvas.toBlob(function (blob) {
          if (blob) {
            var jpegName = file.name.replace(/\.hei[cf]$/i, '.jpg') || 'photo.jpg';
            callback(new File([blob], jpegName, { type: 'image/jpeg' }));
          } else { callback(null); }
        }, 'image/jpeg', 0.92);
      } catch (e) { URL.revokeObjectURL(url); callback(null); }
    };
    img.onerror = function () { URL.revokeObjectURL(url); callback(null); };
    img.src = url;
  }

  function applyPhoto(idx, file, restoredCrop) {
    if (photos[idx] && photos[idx].url) releaseUrl(photos[idx].url);
    var url = URL.createObjectURL(file);
    /* A new file means any crop from the old one is meaningless — back to
       auto-centred until the customer says otherwise. */
    photos[idx] = { file: file, url: url, natW: 0, natH: 0, crop: null };

    (function (slot) {
      loadImage(url, function (img) {
        if (!img || photos[idx] !== slot) return;
        slot.natW = img.naturalWidth;
        slot.natH = img.naturalHeight;
        if (restoredCrop) {
          slot.crop = {
            x: restoredCrop.x * slot.natW,
            y: restoredCrop.y * slot.natH,
            w: restoredCrop.w * slot.natW,
            h: restoredCrop.h * slot.natH,
          };
        }
        markCellDirty(idx);
        scheduleCellRepaint();
        updateQuality();
      });
    })(photos[idx]);

    var cell     = document.querySelector('.cc-cell[data-idx="' + idx + '"]');
    var cellImg  = g('cc-cell-img-' + idx);
    var cellCtrl = g('cc-cell-controls-' + idx);
    var cellPh   = g('cc-placeholder-' + idx);
    if (cellImg)  { cellImg.src = url; cellImg.style.display = 'block'; }
    if (cellCtrl) cellCtrl.style.display = 'flex';
    if (cellPh)   cellPh.style.display = 'none';
    if (cell)     { cell.classList.add('has-photo'); cell.classList.remove('cc-cell--missing'); }

    markCellDirty(idx);
    scheduleCellRepaint();

    var grid = g('cc-photo-grid'), prog = g('cc-upload-progress'), shuf = g('cc-shuffle-row');
    if (grid && grid.style.display === 'none') grid.style.display = '';
    if (prog && prog.style.display === 'none') prog.style.display = '';
    if (shuf && shuf.style.display === 'none') shuf.style.display = '';

    updateUploadCount();
    setupCellDnd();
    scheduleWatermark();
    scheduleDraftSave();
  }

  function updateUploadCount() {
    var n = 0;
    for (var i = 0; i < state.gridCount; i++) { if (photos[i]) n++; }
    var ce = g('cc-upload-count'); if (ce) ce.textContent = n;
    var fe = g('cc-upload-progress-fill'); if (fe) fe.style.width = Math.round((n / state.gridCount) * 100) + '%';
  }

  /* ── FILTER ──────────────────────────────────── */
  function applyFilterToAll() {
    repaintAllPosterCells();
    if (state.step === STEP_CROP) requestAnimationFrame(paintCropTiles);
    scheduleDraftSave();
  }

  /* ── BACKGROUND ────────────────────────────────── */
  function applyBackground() {
    var cfg = BG_CONFIG[state.bg] || BG_CONFIG.white;
    var poster = g('cc-poster'); if (!poster) return;
    poster.style.background = cfg.bg;
    var header = g('cc-poster-header'), hl = g('cc-p-headline'), sl = g('cc-p-subline');
    if (header) header.style.color = cfg.text;
    if (hl) hl.style.color = cfg.text;
    if (sl) sl.style.color = cfg.muted;
    document.querySelectorAll('.cc-poster-cell-empty').forEach(function (el) { el.style.background = cfg.cell; });
    scheduleDraftSave();
  }

  /* ── RATIO ───────────────────────────────────── */
  function updatePosterRatio(key) {
    var poster = g('cc-poster'); if (!poster) return;
    var cfg = SIZE_CONFIG[key]; if (!cfg) return;
    Object.keys(SIZE_CONFIG).forEach(function (k) { poster.classList.remove(SIZE_CONFIG[k].ratio); });
    poster.classList.add(cfg.ratio);
  }

  /* ── PREVIEW TEXT ───────────────────────────────── */
  function updatePreview() {
    var v = getValues();
    var pN = g('cc-p-names'), pD = g('cc-p-date'), pH = g('cc-p-headline'), pS = g('cc-p-subline');
    var pDiv = g('cc-p-divider');
    if (pN) pN.textContent = v.names;

    /* The date is optional and has no stand-in: no date means no divider
       either, and the names sit dead centre on their own. The two are hidden
       rather than emptied because the header is a flex row with a gap — blank
       children still take their share of it and would push the names off
       centre by a few pixels. */
    var hasDate = !!v.date;
    if (pD)   { pD.textContent = v.date; pD.style.display = hasDate ? '' : 'none'; }
    if (pDiv) pDiv.style.display = hasDate ? '' : 'none';

    if (pH) pH.textContent = v.headline || 'forever starts here';
    if (pS) pS.textContent = v.subline ? v.subline.toUpperCase() : '';
  }

  function getValues() {
    var n1  = (g('cc-name1') && g('cc-name1').value.trim().toUpperCase()) || '';
    var sep = (g('cc-sep') && g('cc-sep').value.trim()) || '&';
    var n2  = (g('cc-name2') && g('cc-name2').value.trim().toUpperCase()) || '';
    var namesDisplay = (n1 || n2)
      ? ((n1 || 'NAME 1') + ' ' + sep + ' ' + (n2 || 'NAME 2'))
      : 'AHMED & SARA';
    return {
      names:    namesDisplay,
      /* Genuinely optional — an unfilled date prints nothing at all, rather
         than a specimen date the customer never chose. */
      date:     (g('cc-date') && g('cc-date').value.trim()) || '',
      headline: (g('cc-headline') && g('cc-headline').value.trim()) || '',
      subline:  (g('cc-subline') && g('cc-subline').value.trim()) || '',
    };
  }

  /* ── VARIANT ─────────────────────────────────── */
  function money(cents) {
    return (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' EGP';
  }

  function resolveVariant() {
    var variants = window.ccVariants, vi = g('cc-variant-id'), ab = g('cc-atc-btn');
    if (!variants || !vi) return;
    var names = Object.keys(state.selectedOptions), found = null;
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      if (names.every(function (n, idx) { return v['option' + (idx + 1)] === state.selectedOptions[n]; })) { found = v; break; }
    }
    if (!found) return;
    vi.value = found.id;

    var showCompare = !!(found.compareAt && found.compareAt > found.price);
    document.querySelectorAll('.cc-js-price').forEach(function (el) { el.textContent = money(found.price); });
    document.querySelectorAll('.cc-js-price-compare').forEach(function (el) {
      if (showCompare) { el.textContent = money(found.compareAt); el.style.display = ''; }
      else { el.style.display = 'none'; }
    });

    if (ab && !ab.dataset.sparkBusy) {
      ab.disabled = !found.available;
      ab.textContent = found.available ? '🛒 Add to Cart' : 'Out of Stock';
    }
  }

  /* ── FRAME MOCKUP ───────────────────────────────── */
  var FRAME_MATERIALS = {
    'Premium Wood': { w: 20 },
    'Hard Fiber':   { w: 12 },
  };
  var FRAME_FINISHES = {
    'Black':        { face: 'linear-gradient(145deg,#3d3d3d 0%,#161616 45%,#000000 100%)', edge: 'rgba(255,255,255,0.10)' },
    'White':        { face: 'linear-gradient(145deg,#ffffff 0%,#efefef 45%,#d6d6d6 100%)', edge: 'rgba(0,0,0,0.14)' },
    'Light Wooden': { face: 'linear-gradient(145deg,#e3c093 0%,#c89c62 45%,#a67c46 100%)', edge: 'rgba(0,0,0,0.16)' },
  };

  function chosenFrom(map) {
    for (var k in state.selectedOptions) {
      if (!Object.prototype.hasOwnProperty.call(state.selectedOptions, k)) continue;
      if (map[state.selectedOptions[k]]) return state.selectedOptions[k];
    }
    return null;
  }

  function applyFrame(show, snap) {
    var shell = document.querySelector('.cc-poster-shell');
    if (!shell) return;

    if (!show) {
      shell.classList.remove('cc-framed', 'cc-frame-snap');
      return;
    }

    var mat = FRAME_MATERIALS[chosenFrom(FRAME_MATERIALS)] || { w: 14 };
    var fin = FRAME_FINISHES[chosenFrom(FRAME_FINISHES)]   || FRAME_FINISHES['Black'];

    shell.style.setProperty('--cc-frame-w',    mat.w + 'px');
    shell.style.setProperty('--cc-frame-face', fin.face);
    shell.style.setProperty('--cc-frame-edge', fin.edge);

    var wasFramed = shell.classList.contains('cc-framed');
    shell.classList.add('cc-framed');

    if (snap || !wasFramed) {
      shell.classList.remove('cc-frame-snap');
      void shell.offsetWidth;
      shell.classList.add('cc-frame-snap');
    }
    scheduleWatermark();
  }

  /* ── WATERMARK ────────────────────────────────── */
  function scheduleWatermark() {
    if (watermarkRaf) cancelAnimationFrame(watermarkRaf);
    watermarkRaf = requestAnimationFrame(drawWatermarkOverlay);
  }

  function drawWatermarkOverlay() {
    watermarkRaf = null;
    for (var i = 0; i < state.gridCount; i++) {
      var canvas = g('cc-cell-wm-' + i);
      var cell   = g('cc-poster-cell-' + i);
      if (!canvas || !cell) continue;
      var W = cell.offsetWidth, H = cell.offsetHeight;
      if (!W || !H) continue;
      var dpr = window.devicePixelRatio || 1;
      canvas.width  = W * dpr; canvas.height = H * dpr;
      canvas.style.width = W + 'px'; canvas.style.height = H + 'px';
      canvas.style.display = 'block';
      var ctx = canvas.getContext('2d'); ctx.scale(dpr, dpr);
      drawWatermark(ctx, W, H);
    }
  }

  function drawWatermark(ctx, W, H) {
    ctx.save();
    var fontSize = Math.max(8, Math.round(W * 0.08));
    ctx.font         = 'bold ' + fontSize + 'px sans-serif';
    ctx.fillStyle    = 'rgba(80,80,80,0.28)';
    ctx.textAlign    = 'center';
    ctx.textBaseline = 'middle';
    var text  = 'SPARK PREVIEW';
    var angle = -Math.PI / 6;
    var textW = ctx.measureText(text).width;
    var stepX = textW + fontSize * 1.5;
    var stepY = fontSize * 2.5;
    ctx.translate(W / 2, H / 2);
    ctx.rotate(angle);
    var d = Math.ceil(Math.sqrt(W * W + H * H));
    for (var row = -d; row < d; row += stepY) {
      for (var col = -d; col < d; col += stepX) {
        ctx.fillText(text, col, row);
      }
    }
    ctx.restore();
  }

  /* ── SHUFFLE ─────────────────────────────────── */
  function shufflePhotos() {
    pushOrderHistory();
    var n = state.gridCount;
    for (var i = n - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      if (i !== j) swapPhotos(i, j);
    }
  }

  /* ── DRAG AND DROP REORDER ─────────────────────── */
  var dnd = { dragIdx: -1 };

  function initDragDrop() { setupCellDnd(); }

  function setupCellDnd() {
    document.querySelectorAll('.cc-cell').forEach(function (cell) {
      if (cell._dndBound) return;
      cell._dndBound = true;
      var idx = parseInt(cell.dataset.idx, 10);

      cell.setAttribute('draggable', 'true');
      cell.addEventListener('dragstart', function (e) {
        if (!photos[idx]) { e.preventDefault(); return; }
        dnd.dragIdx = idx; cell.classList.add('cc-dragging');
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', String(idx));
      });
      cell.addEventListener('dragend', function () {
        cell.classList.remove('cc-dragging'); clearDragOver(); dnd.dragIdx = -1;
      });
      cell.addEventListener('dragover', function (e) {
        if (dnd.dragIdx < 0 || dnd.dragIdx === idx) return;
        e.preventDefault(); e.dataTransfer.dropEffect = 'move';
        clearDragOver(); cell.classList.add('cc-drag-over');
      });
      cell.addEventListener('dragleave', function (e) {
        if (!cell.contains(e.relatedTarget)) cell.classList.remove('cc-drag-over');
      });
      cell.addEventListener('drop', function (e) {
        e.preventDefault(); cell.classList.remove('cc-drag-over');
        var from = dnd.dragIdx, to = idx;
        if (from < 0 || from === to) return;
        pushOrderHistory();
        swapPhotos(from, to); dnd.dragIdx = -1;
      });

      var touchClone = null, touchFrom = -1;
      cell.addEventListener('touchstart', function (e) {
        var handle = e.target.closest('.cc-drag-handle');
        if (!handle) return;
        if (!photos[idx]) return;
        touchFrom = idx; cell.classList.add('cc-dragging');
        var rect = cell.getBoundingClientRect();
        touchClone = cell.cloneNode(true);
        touchClone.style.cssText = [
          'position:fixed', 'width:' + rect.width + 'px', 'height:' + rect.height + 'px',
          'top:' + rect.top + 'px', 'left:' + rect.left + 'px', 'opacity:0.85',
          'pointer-events:none', 'z-index:9998', 'border-radius:6px', 'overflow:hidden',
          'box-shadow:0 8px 24px rgba(0,0,0,0.25)', 'transform:scale(1.05)', 'transition:none',
        ].join(';');
        document.body.appendChild(touchClone);
      }, { passive: true });

      cell.addEventListener('touchmove', function (e) {
        if (touchFrom < 0) return;
        e.preventDefault();
        var touch = e.touches[0];
        if (touchClone) {
          var rect = touchClone.getBoundingClientRect();
          touchClone.style.left = (touch.clientX - rect.width / 2) + 'px';
          touchClone.style.top  = (touch.clientY - rect.height / 2) + 'px';
        }
        clearDragOver();
        if (touchClone) touchClone.style.display = 'none';
        var el = document.elementFromPoint(touch.clientX, touch.clientY);
        if (touchClone) touchClone.style.display = '';
        var target = el && el.closest('.cc-cell');
        if (target && target !== cell) target.classList.add('cc-drag-over');
      }, { passive: false });

      cell.addEventListener('touchend', function (e) {
        if (touchFrom < 0) return;
        var touch = e.changedTouches[0];
        cell.classList.remove('cc-dragging');
        if (touchClone) { document.body.removeChild(touchClone); touchClone = null; }
        clearDragOver();
        var el = document.elementFromPoint(touch.clientX, touch.clientY);
        var target = el && el.closest('.cc-cell');
        if (target && target !== cell) {
          var toIdx = parseInt(target.dataset.idx, 10);
          if (!isNaN(toIdx) && toIdx !== touchFrom) { pushOrderHistory(); swapPhotos(touchFrom, toIdx); }
        }
        touchFrom = -1;
      }, { passive: true });
    });
  }

  function clearDragOver() {
    document.querySelectorAll('.cc-drag-over').forEach(function (el) { el.classList.remove('cc-drag-over'); });
  }

  /* ── SWAP PHOTOS ──────────────────────────────── */
  function paintCell(idx) {
    var cell     = document.querySelector('.cc-cell[data-idx="' + idx + '"]');
    var cellImg  = g('cc-cell-img-' + idx);
    var cellCtrl = g('cc-cell-controls-' + idx);
    var cellPh   = g('cc-placeholder-' + idx);
    if (photos[idx]) {
      if (cellImg)  { cellImg.src = photos[idx].url; cellImg.style.display = 'block'; }
      if (cellCtrl) cellCtrl.style.display = 'flex';
      if (cellPh)   cellPh.style.display = 'none';
      if (cell)     cell.classList.add('has-photo');
    } else {
      if (cellImg)  { cellImg.src = ''; cellImg.style.display = 'none'; }
      if (cellCtrl) cellCtrl.style.display = 'none';
      if (cellPh)   cellPh.style.display = 'flex';
      if (cell)     cell.classList.remove('has-photo');
    }
    markCellDirty(idx);
    scheduleCellRepaint();
  }

  /* Crops travel with their photo, because the crop lives on the slot object
     that is being moved — reordering never re-crops anything. */
  function swapPhotos(a, b) {
    var tmp = photos[a]; photos[a] = photos[b]; photos[b] = tmp;
    paintCell(a); paintCell(b);
    scheduleWatermark();
    updateQuality();
    scheduleDraftSave();
  }

  /* ── ORDER HISTORY (undo) ───────────────────────── */
  var orderHistory = [];

  function pushOrderHistory() {
    orderHistory.push(photos.slice());
    if (orderHistory.length > 20) orderHistory.shift();
    var btn = g('cc-undo-btn');
    if (btn) btn.style.display = '';
  }

  function undoOrder() {
    if (!orderHistory.length) return;
    var prev = orderHistory.pop();
    for (var i = 0; i < 12; i++) { photos[i] = prev[i] || null; }
    for (var j = 0; j < 12; j++) { paintCell(j); }
    scheduleWatermark();
    updateQuality();
    if (!orderHistory.length) {
      var btn = g('cc-undo-btn');
      if (btn) btn.style.display = 'none';
    }
  }

  /* ── CANVAS RENDER ───────────────────────────────
     The printed file is built from the same measurements as the live preview:
     each cell's own rectangle is read off the DOM and scaled up, so what the
     customer arranged, cropped and approved is exactly what is produced. */
  function renderPosterCanvas(W, H, withWatermark, callback) {
    var bgCfg = BG_CONFIG[state.bg] || BG_CONFIG.white;
    var v     = getValues();

    var poster     = g('cc-poster');
    var headerEl   = g('cc-poster-header');
    var headlineEl = g('cc-p-headline');
    var sublineEl  = g('cc-p-subline');

    var layout = withFrameOff(function () {
      var domW = poster ? poster.offsetWidth : 340;
      var s    = W / domW;
      var out  = { s: s, cells: [], namesY: 0, headlineY: 0, sublineY: 0, namesFs: 9 * s, headlineFs: 26 * s, sublineFs: 7 * s };
      if (!poster) return out;

      var pRect = poster.getBoundingClientRect();
      for (var i = 0; i < state.gridCount; i++) {
        var el = g('cc-poster-cell-' + i);
        if (!el) continue;
        var r = el.getBoundingClientRect();
        out.cells[i] = {
          x: (r.left - pRect.left) * s,
          y: (r.top  - pRect.top)  * s,
          w: r.width  * s,
          h: r.height * s,
        };
      }

      function centreY(el) {
        if (!el) return 0;
        var r = el.getBoundingClientRect();
        return (r.top + r.height / 2 - pRect.top) * s;
      }
      function fontPx(el, fallback) {
        if (!el) return fallback * s;
        var fs = parseFloat(window.getComputedStyle(el).fontSize);
        return (isFinite(fs) && fs > 0 ? fs : fallback) * s;
      }

      out.namesY     = centreY(headerEl)   || 22 * s;
      out.headlineY  = centreY(headlineEl) || H - 40 * s;
      out.sublineY   = centreY(sublineEl)  || H - 20 * s;
      out.namesFs    = fontPx(headerEl, 9);
      out.headlineFs = fontPx(headlineEl, 26);
      out.sublineFs  = fontPx(sublineEl, 7);
      return out;
    });

    /* The total is counted in full BEFORE a single load starts. loadImage calls
       back synchronously on a cache hit, and by the time anyone submits every
       photo is cached from painting the preview — so counting and loading in
       one pass made `loaded === needed` true on every iteration, firing the
       render once per photo. That produced one add-to-cart request per photo.
       `fired` is the belt to that braces. */
    var targets = [];
    for (var i = 0; i < state.gridCount; i++) { if (photos[i]) targets.push(i); }

    var needed = targets.length, loaded = 0, imgEls = [], fired = false;

    function go() {
      if (fired) return;
      fired = true;
      paint();
    }

    if (!needed) {
      go();
    } else {
      targets.forEach(function (idx) {
        loadImage(photos[idx].url, function (img) {
          imgEls[idx] = img;
          loaded++;
          if (loaded === needed) go();
        });
      });
    }

    function paint() {
      var canvas = document.createElement('canvas');
      canvas.width = W; canvas.height = H;
      var ctx = canvas.getContext('2d');

      ctx.fillStyle = bgCfg.bg; ctx.fillRect(0, 0, W, H);

      for (var idx = 0; idx < state.gridCount; idx++) {
        var box = layout.cells[idx];
        if (!box) continue;
        ctx.fillStyle = bgCfg.cell;
        ctx.fillRect(box.x, box.y, box.w, box.h);

        var img = imgEls[idx];
        var p   = photos[idx];
        if (!img || !p) continue;
        if (!p.natW) { p.natW = img.naturalWidth; p.natH = img.naturalHeight; }
        var rect = cropFor(p, box.w / box.h);
        if (!rect) continue;
        ctx.save();
        ctx.beginPath(); ctx.rect(box.x, box.y, box.w, box.h); ctx.clip();
        drawPhoto(ctx, img, rect, box.x, box.y, box.w, box.h, state.filter);
        ctx.restore();
      }

      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';

      ctx.fillStyle = bgCfg.text;
      ctx.font = '300 ' + Math.round(layout.namesFs) + 'px "DM Sans",sans-serif';
      /* Matches the live preview exactly: the divider only exists to separate
         two things, so with no date the names are drawn alone and centred. */
      ctx.fillText(v.date ? v.names + '   |   ' + v.date : v.names, W / 2, layout.namesY);

      ctx.font = '500 ' + Math.round(layout.headlineFs) + 'px "Caveat",cursive';
      ctx.fillStyle = bgCfg.text;
      ctx.fillText(v.headline || 'forever starts here', W / 2, layout.headlineY);

      if (v.subline) {
        ctx.font = '300 ' + Math.round(layout.sublineFs) + 'px "DM Sans",sans-serif';
        ctx.fillStyle = bgCfg.muted;
        ctx.fillText(v.subline.toUpperCase(), W / 2, layout.sublineY);
      }

      if (withWatermark) drawWatermark(ctx, W, H);
      callback(canvas);
    }
  }

  /* ── PRODUCE FILES ──────────────────────────────── */
  function renderPosterToFile(callback) {
    var cfg   = SIZE_CONFIG[state.currentSize] || SIZE_CONFIG[DEFAULT_SIZE];
    var CM    = 1 / 2.54;
    var fullW = Math.round(cfg.wCm * CM * MIN_DPI);
    var fullH = Math.round(cfg.hCm * CM * MIN_DPI);
    var prevW = 220;
    var prevH = Math.round(prevW * cfg.hCm / cfg.wCm);

    renderPosterCanvas(fullW, fullH, false, function (fullCanvas) {
      if (!fullCanvas) { callback(null, null); return; }
      fullCanvas.toBlob(function (blob) {
        if (!blob) { callback(null, null); return; }
        var cleanFile = new File([blob], 'collage-poster.jpg', { type: 'image/jpeg' });
        renderPosterCanvas(prevW, prevH, true, function (prevCanvas) {
          if (!prevCanvas) { callback(cleanFile, null); return; }
          prevCanvas.toBlob(function (blob2) {
            callback(cleanFile, blob2 ? new File([blob2], 'collage-poster-preview.jpg', { type: 'image/jpeg' }) : null);
          }, 'image/jpeg', 0.65);
        });
      }, 'image/jpeg', 0.93);
    });
  }

  /* ── CART REFRESH ───────────────────────────────── */
  function sparkCartEl() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  function sparkWithSections(formData) {
    var cart = sparkCartEl();
    if (cart && typeof cart.getSectionsToRender === 'function') {
      formData.append('sections', cart.getSectionsToRender().map(function (s) { return s.id; }));
      formData.append('sections_url', window.location.pathname);
      if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
    }
    return formData;
  }

  function sparkCartRefresh(response, source) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: source,
        productVariantId: response && response.variant_id,
        cartData: response,
      });
    }
    var cart = sparkCartEl();
    if (!cart) return;
    cart.classList.remove('is-empty');
    if (response && response.sections && typeof cart.renderContents === 'function') {
      cart.renderContents(response);
    } else if (typeof cart.open === 'function') {
      cart.open();
    }
  }

  /* ── SUBMIT ──────────────────────────────────── */
  function handleSubmit(e) {
    e.preventDefault(); e.stopImmediatePropagation();
    var missing = [];
    for (var i = 0; i < state.gridCount; i++) { if (!photos[i]) missing.push(i + 1); }
    if (missing.length > 0) {
      showError('Please upload all ' + state.gridCount + ' photos. Missing: ' + missing.join(', ') + '.');
      goToStep(STEP_PHOTOS); return;
    }
    var name1    = (g('cc-name1') && g('cc-name1').value.trim()) || '';
    var name2    = (g('cc-name2') && g('cc-name2').value.trim()) || '';
    var headline = (g('cc-headline') && g('cc-headline').value.trim()) || '';
    if (!name1 || !name2) {
      if (!name1 && g('cc-name1')) g('cc-name1').classList.add('cc-field--error');
      if (!name2 && g('cc-name2')) g('cc-name2').classList.add('cc-field--error');
      showError('Please enter both names.'); goToStep(STEP_TEXT); return;
    }
    if (!headline) {
      if (g('cc-headline')) g('cc-headline').classList.add('cc-field--error');
      showError('Please enter a headline.'); goToStep(STEP_TEXT); return;
    }
    var vi = g('cc-variant-id');
    if (!vi || !vi.value) { showError('Please select all options.'); return; }
    var atcBtn = g('cc-atc-btn');
    if (atcBtn && atcBtn.dataset.sparkBusy) return;
    hideError(); buildReview();
    var spin = showSpinner(atcBtn);
    spin.label('Rendering poster…');
    /* Nothing may post to the cart twice for one click. The render pipeline is
       callback-driven, and a counting mistake in it once fired this callback
       once per photo — which added the poster to the cart nine times over. A
       render bug should stay a render bug, not become a duplicate order. */
    var posted = false;
    setTimeout(function () {
      renderPosterToFile(function (cleanFile, previewFile) {
        if (posted) return;
        if (!cleanFile) { showError('Could not render poster. Please try again.'); spin.restore(); return; }
        posted = true;
        var hp = g('cc-hidden-photo'), pp = g('cc-preview-photo');
        try { var dt1 = new DataTransfer(); dt1.items.add(cleanFile); hp.files = dt1.files; }
        catch (err) { showError('Upload not supported. Please use Chrome or Safari.'); spin.restore(); return; }
        if (previewFile && pp) { try { var dt2 = new DataTransfer(); dt2.items.add(previewFile); pp.files = dt2.files; } catch (ex) {} }
        spin.label('Uploading…');
        fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          body: sparkWithSections(new FormData(g('cc-product-form'))),
        })
          /* Read the body as text and parse it ourselves. Calling res.json() on
             an error meant that whenever the cart replied with an HTML page —
             a throttle, a timeout, a size rejection on the uploaded artwork —
             the customer was shown the raw parser message ("JSON.parse:
             unexpected character…") instead of anything they could act on. */
          .then(function (res) {
            return res.text().then(function (body) {
              var data = null;
              try { data = JSON.parse(body); } catch (e) {}
              if (!res.ok) {
                throw new Error((data && (data.description || data.message)) ||
                                'The cart could not accept this order. Please try again in a moment.');
              }
              if (!data) throw new Error('The cart gave an unexpected response. Please try again in a moment.');
              return data;
            });
          })
          .then(function (response) {
            spin.label('Added! ✓');
            setTimeout(function () { spin.restore(); }, 1200);
            draftClear();
            resetCustomizer();
            sparkCartRefresh(response, 'custom-collage');
          })
          .catch(function (err) { showError(err.message || 'Something went wrong.'); spin.restore(); });
      });
    }, 50);
  }

  /* ── RESET ───────────────────────────────────── */
  function resetCustomizer() {
    closeCropper();
    for (var i = 0; i < 12; i++) {
      if (photos[i] && photos[i].url) releaseUrl(photos[i].url);
      photos[i] = null;
      var ci = g('cc-cell-img-' + i); if (ci) { ci.src = ''; ci.style.display = 'none'; }
      var cc = g('cc-cell-controls-' + i); if (cc) cc.style.display = 'none';
      var cp = g('cc-placeholder-' + i); if (cp) cp.style.display = 'flex';
      var cell = document.querySelector('.cc-cell[data-idx="' + i + '"]');
      if (cell) cell.classList.remove('has-photo', 'cc-cell--missing', 'cc-cell--lowres');
      var pi = g('cc-poster-img-' + i); if (pi) { pi.src = ''; pi.style.display = 'none'; }
      var pcell = g('cc-poster-cell-' + i);
      var pcv = pcell && pcell.querySelector('.cc-poster-cell-canvas');
      if (pcv) pcv.style.display = 'none';
      try { var ri = g('cc-replace-' + i); if (ri) ri.files = new DataTransfer().files; } catch (e) {}
    }
    var bi = g('cc-bulk-input'); try { if (bi) bi.files = new DataTransfer().files; } catch (e) {}
    var grid = g('cc-photo-grid'), prog = g('cc-upload-progress'), shuf = g('cc-shuffle-row');
    if (grid) grid.style.display = 'none';
    if (prog) prog.style.display = 'none';
    if (shuf) shuf.style.display = 'none';

    ['cc-name1', 'cc-name2', 'cc-date', 'cc-headline', 'cc-subline'].forEach(function (id) {
      var el = g(id);
      if (el) { el.value = ''; el.classList.remove('cc-field--error'); }
    });
    var sep = g('cc-sep'); if (sep) sep.value = '&';

    state.filter = 'bw'; state.bg = 'white'; state.gridCount = 9;
    document.querySelectorAll('.cc-filter-btn').forEach(function (b) { b.classList.toggle('active', b.dataset.filter === 'bw'); });
    document.querySelectorAll('.cc-bg-btn').forEach(function (b) { b.classList.toggle('active', b.dataset.bg === 'white'); });
    document.querySelectorAll('.cc-count-btn').forEach(function (b) { b.classList.toggle('active', b.dataset.count === '9'); });
    setGridCount(9); applyBackground();

    var seen = {};
    document.querySelectorAll('.cc-pill[data-option]').forEach(function (pill) {
      var opt = pill.dataset.option;
      if (!seen[opt]) {
        seen[opt] = true; pill.classList.add('active'); state.selectedOptions[opt] = pill.dataset.value;
        if (pill.dataset.value in SIZE_CONFIG) { state.currentSize = pill.dataset.value; updatePosterRatio(pill.dataset.value); }
      } else pill.classList.remove('active');
    });
    try { var hp = g('cc-hidden-photo'); if (hp) hp.files = new DataTransfer().files; } catch (e) {}
    try { var pp = g('cc-preview-photo'); if (pp) pp.files = new DataTransfer().files; } catch (e) {}
    orderHistory = [];
    var undoBtn = g('cc-undo-btn'); if (undoBtn) undoBtn.style.display = 'none';
    var phraseSel = g('cc-phrase'); if (phraseSel) phraseSel.selectedIndex = 0;
    applyPhraseSelection(false);
    updateQuality();
    hideError(); updatePreview(); resolveVariant(); goToStep(STEP_PHOTOS); scheduleWatermark();
  }

  /* ── DRAFTS ──────────────────────────────────── */
  var DRAFT_KEY   = 'spark-draft-collage';
  var DRAFT_STORE = 'photos';

  function draftDB(cb) {
    try {
      if (!window.indexedDB) return cb(null);
      var req = indexedDB.open('spark-drafts', 1);
      req.onupgradeneeded = function () {
        var db = req.result;
        if (!db.objectStoreNames.contains(DRAFT_STORE)) db.createObjectStore(DRAFT_STORE);
      };
      req.onsuccess = function () { cb(req.result); };
      req.onerror   = function () { cb(null); };
    } catch (e) { cb(null); }
  }

  function draftPutBlobs(value) {
    draftDB(function (db) {
      if (!db) return;
      try {
        var tx = db.transaction(DRAFT_STORE, 'readwrite');
        tx.objectStore(DRAFT_STORE).put(value, DRAFT_KEY);
      } catch (e) {}
    });
  }

  function draftGetBlobs(cb) {
    draftDB(function (db) {
      if (!db) return cb(null);
      try {
        var tx = db.transaction(DRAFT_STORE, 'readonly');
        var r  = tx.objectStore(DRAFT_STORE).get(DRAFT_KEY);
        r.onsuccess = function () { cb(r.result || null); };
        r.onerror   = function () { cb(null); };
      } catch (e) { cb(null); }
    });
  }

  function draftClear() {
    try { localStorage.removeItem(DRAFT_KEY); } catch (e) {}
    draftDB(function (db) {
      if (!db) return;
      try {
        var tx = db.transaction(DRAFT_STORE, 'readwrite');
        tx.objectStore(DRAFT_STORE).delete(DRAFT_KEY);
      } catch (e) {}
    });
    var bar = g('cc-draft');
    if (bar) bar.style.display = 'none';
  }

  var draftTimer = null;
  function scheduleDraftSave() {
    if (draftTimer) clearTimeout(draftTimer);
    draftTimer = setTimeout(saveDraft, 700);
  }

  function fieldVal(id) { var el = g(id); return el ? el.value : ''; }

  /* Crops are stored as fractions of the photo rather than pixels, so a draft
     restores correctly no matter what the browser reports for the decoded
     size of the file it hands back. */
  function draftCrops() {
    return photos.map(function (p) {
      if (!p || !p.crop || !p.natW || !p.natH) return null;
      return {
        x: p.crop.x / p.natW,
        y: p.crop.y / p.natH,
        w: p.crop.w / p.natW,
        h: p.crop.h / p.natH,
      };
    });
  }

  function saveDraft() {
    try {
      var used = 0;
      for (var i = 0; i < photos.length; i++) { if (photos[i]) used++; }
      var meta = {
        v: 2,
        at: Date.now(),
        gridCount: state.gridCount,
        filter: state.filter,
        bg: state.bg,
        size: state.currentSize,
        phrase: fieldVal('cc-phrase'),
        name1: fieldVal('cc-name1'),
        sep: fieldVal('cc-sep'),
        name2: fieldVal('cc-name2'),
        date: fieldVal('cc-date'),
        headline: fieldVal('cc-headline'),
        subline: fieldVal('cc-subline'),
        photoCount: used,
        crops: draftCrops(),
      };
      if (!used && !meta.name1 && !meta.name2 && !meta.date) { draftClear(); return; }
      localStorage.setItem(DRAFT_KEY, JSON.stringify(meta));
      draftPutBlobs(photos.map(function (p) { return p ? p.file : null; }));
    } catch (e) {}
  }

  function readDraftMeta() {
    try {
      var raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return null;
      var meta = JSON.parse(raw);
      if (!meta || (meta.v !== 1 && meta.v !== 2)) return null;
      if ((Date.now() - meta.at) > 7 * 24 * 3600 * 1000) return null;
      return meta;
    } catch (e) { return null; }
  }

  function offerDraft() {
    var meta = readDraftMeta();
    var bar  = g('cc-draft');
    if (!meta || !bar) return;

    var msg = g('cc-draft-msg');
    if (msg) {
      msg.textContent = meta.photoCount
        ? 'You have an unfinished design with ' + meta.photoCount + ' photo' + (meta.photoCount > 1 ? 's' : '') + '.'
        : 'You have an unfinished design.';
    }
    bar.style.display = '';

    var restore = g('cc-draft-restore');
    if (restore) restore.addEventListener('click', function () { restoreDraft(meta); });
    var discard = g('cc-draft-discard');
    if (discard) discard.addEventListener('click', draftClear);
  }

  function restoreDraft(meta) {
    var set = function (id, v) { var el = g(id); if (el && v != null) el.value = v; };
    set('cc-name1', meta.name1); set('cc-sep', meta.sep); set('cc-name2', meta.name2);
    set('cc-date', meta.date);

    var sel = g('cc-phrase');
    if (sel && meta.phrase) { sel.value = meta.phrase; applyPhraseSelection(false); }
    set('cc-headline', meta.headline); set('cc-subline', meta.subline);

    if (meta.filter) {
      state.filter = meta.filter;
      document.querySelectorAll('.cc-filter-btn').forEach(function (b) {
        b.classList.toggle('active', b.dataset.filter === meta.filter);
      });
    }
    if (meta.bg) {
      state.bg = meta.bg;
      document.querySelectorAll('.cc-bg-btn').forEach(function (b) {
        b.classList.toggle('active', b.dataset.bg === meta.bg);
      });
      applyBackground();
    }
    if (meta.gridCount) {
      document.querySelectorAll('.cc-count-btn').forEach(function (b) {
        b.classList.toggle('active', parseInt(b.dataset.count, 10) === meta.gridCount);
      });
      setGridCount(meta.gridCount);
    }
    if (meta.size) {
      document.querySelectorAll('.cc-pill[data-option]').forEach(function (p) {
        if (p.dataset.value === meta.size) p.click();
      });
    }

    draftGetBlobs(function (blobs) {
      if (blobs && blobs.length) {
        for (var i = 0; i < blobs.length; i++) {
          if (blobs[i]) applyPhoto(i, blobs[i], meta.crops && meta.crops[i]);
        }
      }
      updatePreview();
      onGeometryChanged();
    });

    var bar = g('cc-draft');
    if (bar) bar.style.display = 'none';
  }

  /* ── HELPERS ─────────────────────────────────── */
  function showError(msg) {
    var el = g('cc-error'); if (!el) return;
    el.textContent = msg; el.classList.add('visible');
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }
  function hideError() { var el = g('cc-error'); if (el) el.classList.remove('visible'); }

  /* ── BOOT ────────────────────────────────────── */
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

})();
